import { useCallback, useEffect, useRef, useState } from "react";

import { getProvisioning } from "../services/eroundProvisioning";
import { ProvisioningDetail } from "../types/eroundProvisioning";
import { isInFlight } from "../utils/eroundProvisioning";

// The eRound provisioning record of each station on a prospect page (SOPS-148).
// One read per station, in parallel: a prospect has one to three of them, and
// station-ops lists records by status, not by prospect (research R2).

/** How often stations the worker is still on are read again. */
export const IN_FLIGHT_REFRESH_MS = 15_000;

/**
 * When a change is read again after it happened. station-ops applies a prospect
 * change from the snapshot it publishes, usually within seconds: a station read
 * straight after its IDs were assigned has no record yet, and a stage change
 * moves its records only once that snapshot lands.
 */
export const SETTLE_READS_MS = [5_000, 20_000];

export type StationProvisioning =
  | { state: "loading" }
  | { state: "unavailable"; error: unknown }
  // A null record means station-ops does not track the station: the manual path.
  | { state: "loaded"; record: ProvisioningDetail | null };

const read = async (stationId: string): Promise<StationProvisioning> => {
  try {
    return { state: "loaded", record: await getProvisioning(stationId) };
  } catch (error) {
    return { state: "unavailable", error };
  }
};

// `stage` is the prospect's current state: when it changes, every station is
// read again, at once and once the change has settled.
const useStationProvisioning = (stationIds: string[], stage?: string | null) => {
  // A fresh array arrives on every render; its content is what matters.
  const key = stationIds.join(",");
  const [byStation, setByStation] = useState<Record<string, StationProvisioning>>({});
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Only the latest reload may write: an older one still waiting on station-ops
  // must not overwrite what a newer one (or a cleared list) has set.
  const generation = useRef(0);

  const reload = useCallback(async () => {
    const current = ++generation.current;
    const ids = key ? key.split(",") : [];
    // Nothing to read: clear synchronously, and keep the same object when already empty
    // so the page does not re-render for nothing.
    if (ids.length === 0) {
      setByStation((previous) => (Object.keys(previous).length === 0 ? previous : {}));
      return;
    }
    const entries = await Promise.all(ids.map(async (id) => [id, await read(id)] as const));
    if (mounted.current && current === generation.current) setByStation(Object.fromEntries(entries));
  }, [key]);

  useEffect(() => {
    void reload();
  }, [reload, stage]);

  // Only a change needs the settling reads: a page opening on a prospect, its
  // stage arriving with it, reads what station-ops has long since applied.
  const seenStage = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    const before = seenStage.current;
    seenStage.current = stage;
    if (before == null) return undefined;
    const timers = SETTLE_READS_MS.map((ms) => setTimeout(() => void reload(), ms));
    return () => timers.forEach(clearTimeout);
  }, [reload, stage]);

  const anyInFlight = Object.values(byStation).some(
    (entry) => entry.state === "loaded" && entry.record !== null && isInFlight(entry.record.status)
  );

  useEffect(() => {
    if (!anyInFlight) return undefined;
    const timer = setInterval(() => void reload(), IN_FLIGHT_REFRESH_MS);
    return () => clearInterval(timer);
  }, [anyInFlight, reload]);

  return { byStation, reload };
};

export default useStationProvisioning;
